import { useCallback, useMemo, useState } from "react";
import type { ModifierState } from "@/domain/profile-resolver/index";
import type { BoardLayer, Category, Game, KeyboardLayout, Layers } from "@/domain/types";
import { builtins } from "@/hardware/keyboards/builtin";
import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { type ActiveSetup, resolveActiveSetup } from "@/platform/catalog/active-setup";
import { SC2_SOURCES } from "@/platform/catalog/sc2-sources";
import type { LensView } from "@/platform/share/link";
import { computeDiff } from "@/render/compare/diff";
import { useSide } from "./use-side";

/**
 * The compare B-side, owned in one place. The page resolves its own A side, then hands this hook the
 * A-side context (the effective board + layout, the shared inputs, the A layers for the diff); the hook
 * owns all the B-only state - the second Profile/source, B's per-side board + layout, the shared key
 * unit's second surface, and the changes-list hover - and derives the B render bundle through the SAME
 * useSide as the A side, so the two never drift. The B side derives only while comparing (useSide gates
 * the expensive parse/project/compose on `comparing`). Compare A/B state is transient (never persisted);
 * a share link seeds it once through the lazy initializers below and never writes back.
 */
export interface CompareInput {
  /** The share-link lens override, read once at mount to seed compare state. */
  lensView: LensView | undefined;
  /** The active Game, the axis the B-side setup resolution follows. */
  game: Game;
  /**
   * The setup the A side RESOLVED (via the ActiveSetup seam, uploads included). SC2's B side
   * re-resolves with its own upload + selected id; a simple Game's B mirrors this object itself
   * ("Same as A"), so a re-resolution can never drift to a different Profile than the one A renders.
   */
  setup: ActiveSetup;
  imported: Record<string, ImportedKeyboard>;
  contextId: string;
  mods: ModifierState;
  categories: Record<string, Category>;
  rapidFireOn: boolean;
  hideDefaults: boolean;
  /**
   * The A side's EFFECTIVE board (pin / override / persisted resolved); B's "Same as A" follows
   * it, and the B-layout reset keys on the effective B board derived from it.
   */
  effBoardId: string;
  /** The A side's resolved Keyboard Layout; "Same as A" follows its id, and labels its sentinel. */
  keyboardLayout: KeyboardLayout | BoardLayer;
  /** The A side's resolved layers, the diff's A input. */
  layers: Layers;
}

/** Resolve and own the compare B side. Returns its state, the B render bundle, and the diff. */
export function useCompare({
  lensView,
  game,
  setup,
  imported,
  contextId,
  mods,
  categories,
  rapidFireOn,
  hideDefaults,
  effBoardId,
  keyboardLayout,
  layers,
}: CompareInput) {
  const [comparing, setComparing] = useState(() => lensView?.cmp ?? false);
  const [sc2SourceBId, setSc2SourceBId] = useState(
    () => lensView?.pb ?? SC2_SOURCES[1]?.id ?? SC2_SOURCES[0]?.id ?? "",
  );
  const [uploadedB, setUploadedB] = useState<{ text: string; name: string } | null>(null);
  // the link's seeded Layout B: an unbound peek that applies to whatever board B settles on. A
  // preset link can release a persisted board-bound pin one render after mount, so binding the
  // seed to the first-render board would wipe it on that transient; a user pick supersedes it.
  const [layoutBSeed, setLayoutBSeed] = useState<string | null>(() => lensView?.layoutB ?? null);
  // the user's own Layout B picks, kept per board like the A side's layoutSel: a pick made on one
  // board never resolves against another (a stale universal Keyboard Layout or board-layer id
  // must not carry across boards), and returning to a board revives its pick.
  const [layoutBSel, setLayoutBSel] = useState<Record<string, string>>({});
  // the B-side Keyboard (per-side boards): null = "Same as A", else its own board id. The diff still
  // matches by physical position, so positions can read as moved/unbound where the two boards differ.
  // The share-link seed applies only when it resolves for THIS viewer (own-key lookup: a typo'd or
  // hostile ?kbdB naming an Object.prototype member must be dropped, not rendered or crashed on);
  // links only ever carry built-in ids (session-imported boards are gated out at emission).
  const [boardBId, setBoardBId] = useState<string | null>(() =>
    lensView?.kbdB && Object.hasOwn(builtins, lensView.kbdB) ? lensView.kbdB : null,
  );
  // the hovered Changes-list row id, narrowing the diff flashlight to that row's keys
  const [hoverDiffId, setHoverDiffId] = useState<string | null>(null);

  // the B side's own resolved setup: SC2 re-resolves through the same seam with B's own upload +
  // selected id (portableOnly - the B side does not offer board-bound sources: a pinned source must
  // never render on an unpinned board, and an independent pinned B side is deferred); a simple Game
  // mirrors A's resolved setup ("Same as A" is the literal object: an id re-resolution against the
  // built-ins alone would silently swap an uploaded "my own" Profile for stock Default).
  const setupB: ActiveSetup =
    setup.kind === "sc2"
      ? resolveActiveSetup({
          game,
          sc2SourceId: sc2SourceBId,
          sc2Upload: uploadedB,
          selectedProfileId: undefined,
          uploadedProfile: null,
          portableOnly: true,
        })
      : setup;
  // a board-bound Profile mirrored onto B carries its pin with it: the pin sits ABOVE the per-side
  // board choice (as it does on the A side), so B can never render a pinned setup on an unpinned
  // board - the B Keyboard picker locks while it holds. (The SC2 arm never pins: portableOnly
  // drops pinned sources and the stale-id fallback is the unpinned landing default.)
  const boardBPinned = setupB.kind === "profile" && setupB.pin != null;
  // "Same as A" follows the A-side EFFECTIVE board+layout (including an active board-bound pin), not the
  // raw persisted pick - so a pinned A mirrors onto the same board on B, and the "Same as A" label
  // never advertises a board B is not actually showing.
  const boardBEffective = boardBPinned ? effBoardId : (boardBId ?? effBoardId);
  const sameBoardB = boardBEffective === effBoardId;
  // the Layout B in force: this board's own pick, else the link's seed. Derive-validity, not an
  // effect: when the board B renders changes (A's effective board while "Same as A", or B's own
  // pick), a pick recorded for another board simply stops matching - no reset to mis-fire on a
  // mount transient, nothing for StrictMode to double-run.
  const layoutBValue = layoutBSel[boardBEffective] ?? layoutBSeed;
  const pickLayoutB = useCallback(
    (id: string | null) => {
      setLayoutBSeed(null); // an explicit pick supersedes the link's peek
      setLayoutBSel((prev) => {
        const next = { ...prev };
        if (id) next[boardBEffective] = id;
        else delete next[boardBEffective];
        return next;
      });
    },
    [boardBEffective],
  );
  // picking a B board adopts the view as the user's own arrangement, clearing the link's layout
  // peek (mirrors the A side's selectBoard clearing the lens override)
  const pickBoardB = useCallback((id: string | null) => {
    setBoardBId(id);
    setLayoutBSeed(null);
  }, []);
  // the B side through the SAME useSide hook as A, gated on `comparing` so an A-side interaction
  // never re-runs the B pass while compare is closed. "Same as A" follows the A-side effective
  // board+layout; the setup is B's own resolution above (SC2) or A's mirrored object (simple).
  // Independent B-side Profile selection is deferred.
  const {
    entry: entryB,
    board: boardB,
    plusUnavailable: plusUnavailableB,
    table: tableB,
    layers: layersB,
    comboHasExact: comboHasExactB,
    keycaps: keycapsB,
    zones: zonesB,
  } = useSide({
    setup: setupB,
    boardId: boardBEffective,
    layoutId: layoutBValue ?? (sameBoardB ? keyboardLayout.id : undefined),
    imported,
    contextId,
    mods,
    categories,
    rapidFireOn,
    hideDefaults,
    enabled: comparing,
  });
  // the diff over the two full Profiles for the current Context; null off-compare
  const diff = useMemo(
    () => (comparing ? computeDiff(layers, layersB) : null),
    [comparing, layers, layersB],
  );

  return {
    comparing,
    setComparing,
    sc2SourceBId,
    setSc2SourceBId,
    uploadedB,
    setUploadedB,
    boardBId,
    pickBoardB,
    boardBPinned,
    layoutBValue,
    pickLayoutB,
    hoverDiffId,
    setHoverDiffId,
    setupB,
    boardBEffective,
    sameBoardB,
    entryB,
    boardB,
    tableB,
    layersB,
    comboHasExactB,
    keycapsB,
    zonesB,
    plusUnavailableB,
    diff,
  };
}
